/**
 * Canvas renderer for Mushroom Hunt.
 *
 * Draws into a fixed 720x1000 virtual playfield, scaled by CSS via the canvas
 * element style. The HTML wrapper element pins the canvas size to its bounding
 * box; we set canvas.width/height to the device-pixel-ratio adjusted size so
 * the photo images stay crisp.
 *
 * Visual style:
 *   - Dark forest gradient background with parallax tree silhouettes
 *   - Falling mushrooms drawn as circular framed photos with a glowing rim
 *     colored by category. Each mushroom shows its NAME below it.
 *   - Wicker basket drawn from primitives.
 *   - CRT scanlines + vignette overlay applied at the end.
 */

import type { GameSnapshot, FallingMushroom, Particle, FloatText } from "./engine";
import { PLAY_W, PLAY_H } from "./engine";
import type { Difficulty, Language, Mushroom } from "../types";

const COLOR_BG_TOP = "#7e9aa4";
const COLOR_BG_MID = "#b8a888";
const COLOR_BG_HORIZON = "#a07a4a";
const COLOR_BG_BOT = "#2a1a0e";
const COLOR_FOG = "rgba(160, 140, 100, 0.45)";

interface Cloud { x: number; y: number; w: number; speed: number; }
interface Mote { baseX: number; baseY: number; ampX: number; ampY: number; speed: number; phase: number; }

const RIM_COLORS: Record<Mushroom["category"], string> = {
  edible_common: "#7fd96b",
  edible_choice: "#62d1ff",
  edible_rare: "#ffd84d",
  medicinal: "#ff6ec7",
  inedible: "#a8a8a8",
  poison: "#7fff00",
  deadly: "#ff2a2a",
};

const NAME_COLORS: Record<Mushroom["category"], string> = {
  edible_common: "#e8ffe2",
  edible_choice: "#dff5ff",
  edible_rare: "#fff5cc",
  medicinal: "#ffd5ec",
  inedible: "#dcdcdc",
  poison: "#e8ffce",
  deadly: "#ffd5d5",
};

// Advanced mode: identical rim/name colors for every species so players must
// recognize the mushroom from its photo alone.
const NEUTRAL_RIM = "#c8b890";
const NEUTRAL_NAME = "#f1e6c8";

export class Renderer {
  private images = new Map<string, HTMLImageElement>();
  private treeOffsets: number[] = [];
  private treeJitters: number[] = []; // per-tree height variation
  private clouds: Cloud[] = [];
  private motes: Mote[] = []; // floating dust/pollen in sunbeams
  private mountains: { x: number; w: number; h: number }[] = [];
  private difficulty: Difficulty = "beginner";
  private language: Language = "english";

  constructor(private canvas: HTMLCanvasElement) {
    const rng = mulberry32(1337);
    // Parallax tree silhouette positions + per-tree variation
    for (let i = 0; i < 14; i++) {
      this.treeOffsets.push(rng() * PLAY_W);
      this.treeJitters.push(0.78 + rng() * 0.5);
    }
    // A couple of very soft, distant clouds in the upper sky only
    for (let i = 0; i < 3; i++) {
      this.clouds.push({
        x: rng() * PLAY_W,
        y: 50 + rng() * 80,
        w: 120 + rng() * 100,
        speed: 0.002 + rng() * 0.003,
      });
    }
    // A handful of subtle dust motes — kept few so they don't compete
    for (let i = 0; i < 10; i++) {
      this.motes.push({
        baseX: rng() * PLAY_W,
        baseY: 240 + rng() * 520,
        ampX: 30 + rng() * 70,
        ampY: 14 + rng() * 26,
        speed: 0.0003 + rng() * 0.0005,
        phase: rng() * Math.PI * 2,
      });
    }
    // Distant mountain silhouette peaks
    let mx = -40;
    while (mx < PLAY_W + 40) {
      const w = 120 + rng() * 160;
      this.mountains.push({ x: mx, w, h: 80 + rng() * 70 });
      mx += w * (0.55 + rng() * 0.2);
    }
  }

  /**
   * Lazy-load an image for a mushroom. Returns the (possibly still-loading)
   * HTMLImageElement immediately; the renderer falls back to a placeholder
   * disk while `img.complete` is false. The browser HTTP cache + CloudFront
   * de-dupe repeated requests, so calling this every frame is cheap.
   */
  private getImage(m: Mushroom): HTMLImageElement {
    let img = this.images.get(m.id);
    if (!img) {
      img = new Image();
      img.src = m.image_url;
      this.images.set(m.id, img);
    }
    return img;
  }

  setDifficulty(difficulty: Difficulty) {
    this.difficulty = difficulty;
  }

  setLanguage(language: Language) {
    this.language = language;
  }

  /** Pick the display name for a mushroom based on the renderer's language. */
  displayName(m: Mushroom): string {
    if (this.language === "latin") return m.scientific_name;
    if (this.language === "english") return m.english_common_name || m.common_name;
    if (this.language === "russian") return m.russian_common_name || m.scientific_name;
    // "hebrew" — common_name is already Hebrew for israeli_source species;
    // for the rest it stays English (Hebrew not available).
    return m.common_name;
  }

  resize() {
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();
    this.canvas.width = Math.round(rect.width * dpr);
    this.canvas.height = Math.round(rect.height * dpr);
  }

  /**
   * Convert a screen-relative client pointer (e.g. mouse event) to world X.
   */
  clientToWorldX(clientX: number): number {
    const rect = this.canvas.getBoundingClientRect();
    const scale = PLAY_W / rect.width;
    return (clientX - rect.left) * scale;
  }

  render(snap: GameSnapshot, timeMs: number) {
    const ctx = this.canvas.getContext("2d");
    if (!ctx) return;
    const cw = this.canvas.width;
    const ch = this.canvas.height;
    if (!cw || !ch) return;

    // Compute uniform scale to fit playfield in canvas. Letterbox.
    const scale = Math.min(cw / PLAY_W, ch / PLAY_H);
    const offsetX = (cw - PLAY_W * scale) / 2;
    const offsetY = (ch - PLAY_H * scale) / 2;

    // Clear letterbox black
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, cw, ch);

    ctx.save();
    ctx.translate(offsetX, offsetY);
    ctx.scale(scale, scale);

    // Camera shake
    if (snap.shake > 0) {
      const s = snap.shake;
      ctx.translate((Math.random() - 0.5) * s, (Math.random() - 0.5) * s);
    }

    // Clip to playfield to keep effects inside the screen
    ctx.beginPath();
    ctx.rect(0, 0, PLAY_W, PLAY_H);
    ctx.clip();

    this.drawBackground(ctx, timeMs);
    this.drawTrees(ctx);
    this.drawFocusDim(ctx);
    this.drawMotes(ctx, timeMs);
    this.drawGround(ctx);

    // Draw mushrooms behind basket if center-x doesn't overlap horizontally; simpler: draw in order
    for (const f of snap.falling) this.drawFalling(ctx, f, timeMs);

    this.drawBasket(ctx, snap.basket.x, snap.basket.y);

    for (const p of snap.particles) this.drawParticle(ctx, p);
    for (const f of snap.floats) this.drawFloat(ctx, f);

    // Flash overlay (poison/deadly impact)
    if (snap.flash) {
      ctx.fillStyle = snap.flash.color;
      ctx.globalAlpha = Math.max(0, snap.flash.alpha);
      ctx.fillRect(0, 0, PLAY_W, PLAY_H);
      ctx.globalAlpha = 1;
    }

    // CRT scanlines + vignette
    this.drawScanlines(ctx);
    this.drawVignette(ctx);

    ctx.restore();
  }

  // ---------- pieces ----------

  private drawBackground(ctx: CanvasRenderingContext2D, timeMs: number) {
    // Muted morning forest gradient — desaturated so mushrooms stay in focus
    const grad = ctx.createLinearGradient(0, 0, 0, PLAY_H);
    grad.addColorStop(0, COLOR_BG_TOP);
    grad.addColorStop(0.35, "#9aa898");
    grad.addColorStop(0.55, COLOR_BG_MID);
    grad.addColorStop(0.72, COLOR_BG_HORIZON);
    grad.addColorStop(0.88, "#5a3a1e");
    grad.addColorStop(1, COLOR_BG_BOT);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, PLAY_W, PLAY_H);

    // Very faint distant clouds
    for (const c of this.clouds) {
      const x = (c.x + timeMs * c.speed) % (PLAY_W + c.w + 60) - c.w - 30;
      this.drawCloud(ctx, x, c.y, c.w);
    }

    // Diffuse sun glow — no hard disk, just a soft warmth in the upper sky
    const sunX = PLAY_W * 0.78;
    const sunY = 130;
    const halo = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, 260);
    halo.addColorStop(0, "rgba(255, 235, 180, 0.28)");
    halo.addColorStop(0.5, "rgba(255, 220, 160, 0.10)");
    halo.addColorStop(1, "rgba(255, 220, 140, 0)");
    ctx.fillStyle = halo;
    ctx.fillRect(0, 0, PLAY_W, PLAY_H);

    // Distant misty hills — desaturated, low contrast
    ctx.fillStyle = "rgba(110, 122, 122, 0.5)";
    for (const m of this.mountains) {
      const baseY = PLAY_H - 300;
      ctx.beginPath();
      ctx.moveTo(m.x, baseY);
      ctx.lineTo(m.x + m.w * 0.5, baseY - m.h);
      ctx.lineTo(m.x + m.w, baseY);
      ctx.closePath();
      ctx.fill();
    }
    // Warm haze band along the hills
    const mist = ctx.createLinearGradient(0, PLAY_H - 320, 0, PLAY_H - 240);
    mist.addColorStop(0, "rgba(220, 200, 170, 0)");
    mist.addColorStop(1, "rgba(220, 200, 170, 0.22)");
    ctx.fillStyle = mist;
    ctx.fillRect(0, PLAY_H - 320, PLAY_W, 80);

    // One subtle god-ray for atmosphere — faint, no additive blend
    ctx.save();
    const t = timeMs / 6000;
    const sway = Math.sin(t) * 10;
    ctx.translate(sunX - 10 + sway, sunY);
    ctx.rotate(0.55);
    const beam = ctx.createLinearGradient(0, 0, 0, 900);
    beam.addColorStop(0, "rgba(255, 240, 200, 0.10)");
    beam.addColorStop(0.5, "rgba(255, 240, 200, 0.05)");
    beam.addColorStop(1, "rgba(255, 240, 200, 0)");
    ctx.fillStyle = beam;
    ctx.fillRect(-30, 0, 60, 900);
    ctx.restore();
  }

  /** Soft mid-screen dim band — keeps the falling zone calm so mushrooms read clearly. */
  private drawFocusDim(ctx: CanvasRenderingContext2D) {
    const dim = ctx.createLinearGradient(0, 180, 0, PLAY_H - 200);
    dim.addColorStop(0, "rgba(20, 14, 10, 0)");
    dim.addColorStop(0.5, "rgba(20, 14, 10, 0.22)");
    dim.addColorStop(1, "rgba(20, 14, 10, 0)");
    ctx.fillStyle = dim;
    ctx.fillRect(0, 180, PLAY_W, PLAY_H - 380);
  }

  private drawCloud(ctx: CanvasRenderingContext2D, x: number, y: number, w: number) {
    ctx.save();
    ctx.fillStyle = "rgba(255, 252, 244, 0.85)";
    const h = w * 0.32;
    // Cloud is a cluster of overlapping puffs for a soft, fluffy outline
    const puffs = 5;
    for (let i = 0; i < puffs; i++) {
      const t = i / (puffs - 1);
      const px = x + t * w;
      const py = y + Math.sin(t * Math.PI) * -6;
      const pr = h * (0.55 + 0.3 * Math.sin(t * Math.PI));
      ctx.beginPath();
      ctx.arc(px, py, pr, 0, Math.PI * 2);
      ctx.fill();
    }
    // Soft shadow underside
    ctx.fillStyle = "rgba(180, 180, 200, 0.25)";
    ctx.beginPath();
    ctx.ellipse(x + w / 2, y + h * 0.5, w * 0.4, h * 0.25, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private drawMotes(ctx: CanvasRenderingContext2D, timeMs: number) {
    for (const m of this.motes) {
      const x = m.baseX + Math.sin(timeMs * m.speed + m.phase) * m.ampX;
      const y = m.baseY + Math.cos(timeMs * m.speed * 1.3 + m.phase) * m.ampY;
      const pulse = 0.3 + 0.2 * Math.sin(timeMs * 0.003 + m.phase);
      ctx.globalAlpha = pulse;
      ctx.fillStyle = "#e8dcc0";
      ctx.fillRect(Math.floor(x), Math.floor(y), 1, 1);
      ctx.globalAlpha = 1;
    }
  }

  private drawTrees(ctx: CanvasRenderingContext2D) {
    // Back layer — atmospheric haze tint, lighter & cooler for depth
    for (let i = 0; i < this.treeOffsets.length; i++) {
      const x = this.treeOffsets[i];
      const baseY = PLAY_H - 130;
      const j = this.treeJitters[i];
      this.drawPine(ctx, x, baseY, 360 * j, 42, "#5a7a4a", "#3e5a36");
    }
    // Front layer — richer green, fully saturated up close
    for (let i = 0; i < this.treeOffsets.length; i += 2) {
      const x = (this.treeOffsets[i] + 90) % PLAY_W;
      const baseY = PLAY_H - 110;
      const j = this.treeJitters[i] || 1;
      this.drawPine(ctx, x, baseY, 470 * j, 58, "#2a4a24", "#163018");
    }

    // Forest fog
    const fog = ctx.createLinearGradient(0, PLAY_H - 280, 0, PLAY_H - 80);
    fog.addColorStop(0, "rgba(0,0,0,0)");
    fog.addColorStop(1, COLOR_FOG);
    ctx.fillStyle = fog;
    ctx.fillRect(0, PLAY_H - 280, PLAY_W, 200);
  }

  /** Layered conifer pine — stacked triangles with a trunk and shadow side. */
  private drawPine(
    ctx: CanvasRenderingContext2D,
    x: number,
    baseY: number,
    height: number,
    halfBase: number,
    fillColor: string,
    shadowColor: string,
  ) {
    // Trunk
    ctx.fillStyle = "#1a0e08";
    ctx.fillRect(Math.round(x - 4), Math.round(baseY - 22), 8, 22);

    const layers = 4;
    // Each layer is a tapered triangle stacked above the previous one
    for (let i = layers - 1; i >= 0; i--) {
      const ratio = (i + 1) / layers;
      const layerW = halfBase * (0.55 + 0.45 * ratio);
      const top = baseY - (height * (i + 1)) / layers;
      const bot = baseY - (height * i) / layers + 8;
      // Bright side
      ctx.fillStyle = fillColor;
      ctx.beginPath();
      ctx.moveTo(x - layerW, bot);
      ctx.lineTo(x, top);
      ctx.lineTo(x + layerW, bot);
      ctx.closePath();
      ctx.fill();
      // Shadow side (right half) for depth
      ctx.fillStyle = shadowColor;
      ctx.beginPath();
      ctx.moveTo(x, top);
      ctx.lineTo(x + layerW, bot);
      ctx.lineTo(x, bot);
      ctx.closePath();
      ctx.fill();
    }
  }

  private drawGround(ctx: CanvasRenderingContext2D) {
    // Layered soil gradient
    const soil = ctx.createLinearGradient(0, PLAY_H - 80, 0, PLAY_H);
    soil.addColorStop(0, "#4a2814");
    soil.addColorStop(0.4, "#3a1f0e");
    soil.addColorStop(1, "#1f0e06");
    ctx.fillStyle = soil;
    ctx.fillRect(0, PLAY_H - 80, PLAY_W, 80);

    const seed = mulberry32(42);
    // Scattered grass tufts — two tones for depth
    for (let i = 0; i < 60; i++) {
      const x = seed() * PLAY_W;
      const y = PLAY_H - 80 + seed() * 78;
      ctx.fillStyle = "#3a6520";
      ctx.fillRect(Math.floor(x), Math.floor(y), 2, 4);
      ctx.fillStyle = "#6ea832";
      ctx.fillRect(Math.floor(x) + 1, Math.floor(y) - 1, 1, 2);
    }
    // Pebbles + leaves
    for (let i = 0; i < 30; i++) {
      const x = seed() * PLAY_W;
      const y = PLAY_H - 70 + seed() * 65;
      const r = seed();
      if (r < 0.5) {
        ctx.fillStyle = "#6c5638";
        ctx.fillRect(Math.floor(x), Math.floor(y), 3, 2);
      } else {
        ctx.fillStyle = r < 0.75 ? "#a8642a" : "#c48a3a";
        ctx.fillRect(Math.floor(x), Math.floor(y), 4, 2);
        ctx.fillRect(Math.floor(x) + 1, Math.floor(y) + 1, 2, 1);
      }
    }
    // A couple of tiny background mushrooms — muted so they don't distract
    const accentRng = mulberry32(99);
    for (let i = 0; i < 3; i++) {
      const x = accentRng() * PLAY_W;
      const y = PLAY_H - 30 + accentRng() * 20;
      ctx.fillStyle = "#c8b8a0";
      ctx.fillRect(Math.floor(x), Math.floor(y + 3), 2, 3);
      ctx.fillStyle = accentRng() < 0.5 ? "#7a3a2a" : "#8a5a28";
      ctx.fillRect(Math.floor(x) - 1, Math.floor(y), 4, 3);
    }
  }

  private drawFalling(ctx: CanvasRenderingContext2D, f: FallingMushroom, timeMs: number) {
    const img = this.getImage(f.m);
    const r = 68;
    const advanced = this.difficulty === "advanced";
    const rim = advanced ? NEUTRAL_RIM : RIM_COLORS[f.m.category];
    const nameColor = advanced ? NEUTRAL_NAME : NAME_COLORS[f.m.category];

    ctx.save();
    ctx.translate(f.x, f.y);
    ctx.rotate(f.rot);

    // Outer glow ring — pulsing
    const pulse = 0.78 + 0.22 * Math.sin(timeMs * 0.005 + f.x * 0.01);
    const glow = ctx.createRadialGradient(0, 0, r * 0.9, 0, 0, r * 1.9 * pulse);
    glow.addColorStop(0, rim + "e0");
    glow.addColorStop(0.55, rim + "55");
    glow.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = glow;
    ctx.fillRect(-r * 2.2, -r * 2.2, r * 4.4, r * 4.4);

    // Photo masked into a circle
    if (img && img.complete) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(0, 0, r - 6, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      // cover-fit the image inside the circle
      const size = (r - 6) * 2;
      const iw = img.naturalWidth;
      const ih = img.naturalHeight;
      const ratio = Math.max(size / iw, size / ih);
      const dw = iw * ratio;
      const dh = ih * ratio;
      ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
      ctx.restore();
    } else {
      ctx.fillStyle = "#222";
      ctx.beginPath();
      ctx.arc(0, 0, r - 6, 0, Math.PI * 2);
      ctx.fill();
    }

    // Inner dark ring
    ctx.strokeStyle = "rgba(0,0,0,0.35)";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(0, 0, r - 4, 0, Math.PI * 2);
    ctx.stroke();

    // Category rim — thick colored ring
    ctx.strokeStyle = rim;
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.arc(0, 0, r - 1, 0, Math.PI * 2);
    ctx.stroke();

    // Highlight sheen on upper-left of rim
    ctx.strokeStyle = "rgba(255,255,255,0.45)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(0, 0, r - 2, Math.PI * 1.15, Math.PI * 1.55);
    ctx.stroke();

    // One subtle sparkle dot — kept minimal so the mushroom photo stays the focus
    const ang = timeMs * 0.0009 + f.x * 0.01;
    const sx = Math.cos(ang) * (r + 3);
    const sy = Math.sin(ang) * (r + 3);
    const sp = 0.35 + 0.35 * Math.sin(timeMs * 0.008);
    ctx.globalAlpha = sp;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(Math.round(sx) - 1, Math.round(sy) - 1, 2, 2);
    ctx.globalAlpha = 1;

    // Skull / warning markers — only in beginner mode. Advanced players must
    // identify dangerous species by sight.
    if (!advanced) {
      if (f.m.category === "deadly") {
        this.drawSkull(ctx, 0, -r - 12, 20);
      } else if (f.m.category === "poison") {
        this.drawWarning(ctx, 0, -r - 12, 20);
      } else if (f.m.category === "medicinal") {
        this.drawHeart(ctx, 0, -r - 12, 20);
      }
    }

    ctx.restore();

    // Name plate (drawn upright, not rotated). Clamp the plate's center X
    // so it stays fully inside the playfield even when the mushroom itself
    // hugs an edge — otherwise the label gets visually truncated by the
    // playfield clip.
    const labelY = f.y + r + 18;
    const name = this.displayName(f.m).toUpperCase();
    // Press Start 2P has no Hebrew glyphs; for RTL/Hebrew names switch to a
    // sans-serif that actually renders Hebrew and bump the size a bit so it
    // matches the visual weight of the Latin pixel font.
    const hasHebrew = /[֐-׿]/.test(name);
    const fontSize = hasHebrew ? 22 : this.language === "latin" ? 14 : 18;
    ctx.font = hasHebrew
      ? `bold ${fontSize}px "Heebo", "Arial Hebrew", "Noto Sans Hebrew", sans-serif`
      : `bold ${fontSize}px 'Press Start 2P', monospace`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const padding = 12;
    const tw = ctx.measureText(name).width;
    const plateW = tw + padding * 2;
    const plateH = 28;
    const minCX = plateW / 2 + 4;
    const maxCX = PLAY_W - plateW / 2 - 4;
    const cx = Math.max(minCX, Math.min(maxCX, f.x));
    ctx.fillStyle = "rgba(0,0,0,0.7)";
    ctx.fillRect(cx - plateW / 2, labelY - plateH / 2, plateW, plateH);
    ctx.strokeStyle = rim;
    ctx.lineWidth = 2;
    ctx.strokeRect(cx - plateW / 2, labelY - plateH / 2, plateW, plateH);
    ctx.fillStyle = nameColor;
    ctx.fillText(name, cx, labelY + 1);
  }

  private drawSkull(ctx: CanvasRenderingContext2D, x: number, y: number, size: number) {
    ctx.save();
    ctx.translate(x, y);
    ctx.fillStyle = "rgba(0,0,0,0.85)";
    ctx.beginPath();
    ctx.arc(0, 0, size, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#fff";
    // skull dome
    ctx.beginPath();
    ctx.arc(0, -3, size * 0.7, 0, Math.PI * 2);
    ctx.fill();
    // jaw
    ctx.fillRect(-size * 0.45, size * 0.2, size * 0.9, size * 0.35);
    // eyes
    ctx.fillStyle = "#000";
    ctx.beginPath(); ctx.arc(-size * 0.28, -3, size * 0.18, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(size * 0.28, -3, size * 0.18, 0, Math.PI * 2); ctx.fill();
    // teeth gap
    ctx.fillRect(-2, size * 0.2, 4, size * 0.35);
    ctx.restore();
  }

  private drawHeart(ctx: CanvasRenderingContext2D, x: number, y: number, size: number) {
    ctx.save();
    ctx.translate(x, y);
    ctx.fillStyle = "rgba(0,0,0,0.85)";
    ctx.beginPath();
    ctx.arc(0, 0, size, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#ff6ec7";
    const s = size * 0.55;
    ctx.beginPath();
    // Two lobes + bottom point
    ctx.moveTo(0, s * 0.45);
    ctx.bezierCurveTo(s * 1.1, -s * 0.4, s * 0.6, -s * 1.1, 0, -s * 0.35);
    ctx.bezierCurveTo(-s * 0.6, -s * 1.1, -s * 1.1, -s * 0.4, 0, s * 0.45);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  private drawWarning(ctx: CanvasRenderingContext2D, x: number, y: number, size: number) {
    ctx.save();
    ctx.translate(x, y);
    ctx.fillStyle = "#1a1a0a";
    ctx.beginPath();
    ctx.arc(0, 0, size, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#ffd84d";
    ctx.beginPath();
    ctx.moveTo(0, -size * 0.7);
    ctx.lineTo(size * 0.75, size * 0.55);
    ctx.lineTo(-size * 0.75, size * 0.55);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "#000";
    ctx.font = `bold ${size}px 'Press Start 2P', monospace`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("!", 0, 3);
    ctx.restore();
  }

  private drawBasket(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
    const w = 160;
    const h = 80;
    const x = cx - w / 2;
    const y = cy;
    const bodyTopY = y + 8;
    const bodyBotY = y + h;

    // Soft drop shadow on the ground
    const shadow = ctx.createRadialGradient(cx, cy + h + 10, 4, cx, cy + h + 10, w / 2);
    shadow.addColorStop(0, "rgba(0,0,0,0.55)");
    shadow.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = shadow;
    ctx.fillRect(cx - w / 2 - 10, cy + h + 2, w + 20, 22);

    // ---- Handle (drawn first so basket body covers its ends) ----
    this.drawBasketHandle(ctx, cx, y - 4, 70);

    // ---- Body base color (gives weave its warm undertone) ----
    const bodyGrad = ctx.createLinearGradient(0, bodyTopY, 0, bodyBotY);
    bodyGrad.addColorStop(0, "#b87a3a");
    bodyGrad.addColorStop(0.5, "#9c6428");
    bodyGrad.addColorStop(1, "#5a3414");
    ctx.fillStyle = bodyGrad;
    ctx.beginPath();
    // Slightly curved (barrel-shaped) sides — handmade, not perfectly geometric
    ctx.moveTo(x - 4, bodyTopY);
    ctx.quadraticCurveTo(x - 7, (bodyTopY + bodyBotY) / 2, x + 8, bodyBotY);
    ctx.lineTo(x + w - 8, bodyBotY);
    ctx.quadraticCurveTo(x + w + 7, (bodyTopY + bodyBotY) / 2, x + w + 4, bodyTopY);
    ctx.closePath();
    ctx.fill();

    // ---- Vertical "stakes" — the upright sticks the weave goes around ----
    // Drawn first so the horizontal weave can overlap them in alternating cells.
    const stakeCount = 9;
    const stakeXs: number[] = [];
    for (let i = 0; i < stakeCount; i++) {
      const t = i / (stakeCount - 1);
      // Stake follows the curved body edges
      const topX = x + 4 + t * (w - 8);
      const botX = x + 12 + t * (w - 24);
      stakeXs.push(topX);
      // Each stake has a dark core and a small highlight
      ctx.strokeStyle = "#3e2410";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(topX, bodyTopY + 2);
      ctx.lineTo(botX, bodyBotY - 2);
      ctx.stroke();
      ctx.strokeStyle = "#c2843a";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(topX - 1, bodyTopY + 2);
      ctx.lineTo(botX - 1, bodyBotY - 2);
      ctx.stroke();
    }

    // ---- Horizontal weave rows — wavy, slightly irregular, alternating tones ----
    const rows = 7;
    const wickerLight = ["#d49a52", "#c2843a", "#b07028"];
    const wickerDark = "#5a3414";
    for (let row = 0; row < rows; row++) {
      const t = (row + 0.5) / rows;
      const yy = bodyTopY + t * (bodyBotY - bodyTopY);
      // Body half-width at this y (mirrors the quadratic curve above)
      const curveBulge = Math.sin(t * Math.PI) * 7;
      const leftX = x - 4 + (8 + 4 * t) - curveBulge * (1 - t);
      const rightX = x + w + 4 - (8 + 4 * t) + curveBulge * (1 - t);
      // Shadow line under the band — gives depth
      ctx.strokeStyle = "rgba(40, 20, 8, 0.75)";
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.moveTo(leftX, yy + 2);
      this.wickerWave(ctx, leftX, rightX, yy + 2, row);
      ctx.stroke();
      // Bright woven band — color alternates per row for a hand-dyed look
      ctx.strokeStyle = wickerLight[row % wickerLight.length];
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(leftX, yy);
      this.wickerWave(ctx, leftX, rightX, yy, row);
      ctx.stroke();
      // Thin upper highlight
      ctx.strokeStyle = "rgba(255, 220, 160, 0.55)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(leftX, yy - 1.5);
      this.wickerWave(ctx, leftX, rightX, yy - 1.5, row);
      ctx.stroke();

      // Tiny knots where weave passes "over" a stake — gives the woven illusion
      for (let i = 0; i < stakeXs.length; i++) {
        if ((i + row) % 2 !== 0) continue;
        const t2 = i / (stakeCount - 1);
        const sx = leftX + (rightX - leftX) * t2;
        ctx.fillStyle = wickerDark;
        ctx.fillRect(Math.round(sx) - 2, Math.round(yy) - 2, 4, 4);
        ctx.fillStyle = wickerLight[row % wickerLight.length];
        ctx.fillRect(Math.round(sx) - 1, Math.round(yy) - 2, 2, 2);
      }
    }

    // ---- Rim — twisted-rope braid sitting on top of the body ----
    // Back of rim (shadow)
    ctx.fillStyle = "#3e2210";
    ctx.fillRect(x - 8, y - 8, w + 16, 6);
    // Braid bumps — short alternating diagonals
    const braidY = y - 5;
    const braidSegments = 22;
    for (let i = 0; i < braidSegments; i++) {
      const segX = x - 8 + (i / braidSegments) * (w + 16);
      const segW = (w + 16) / braidSegments + 2;
      ctx.fillStyle = i % 2 === 0 ? "#c4823a" : "#8a5320";
      ctx.beginPath();
      ctx.ellipse(segX + segW / 2, braidY, segW * 0.55, 4.5, 0, 0, Math.PI * 2);
      ctx.fill();
      // Tiny highlight
      ctx.fillStyle = "rgba(255, 230, 170, 0.6)";
      ctx.fillRect(Math.round(segX + segW / 2) - 1, Math.round(braidY) - 2, 2, 1);
    }

    // A small leaf tucked into the rim — adds the handmade, foraged feel
    this.drawRimLeaf(ctx, x + 22, y - 6);
  }

  /** Sine-wave wicker strand from leftX to rightX at vertical y, phased by row. */
  private wickerWave(
    ctx: CanvasRenderingContext2D,
    leftX: number,
    rightX: number,
    y: number,
    row: number,
  ) {
    const steps = 24;
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const px = leftX + (rightX - leftX) * t;
      const py = y + Math.sin(t * Math.PI * 4 + row * 0.9) * 1.2;
      ctx.lineTo(px, py);
    }
  }

  private drawBasketHandle(ctx: CanvasRenderingContext2D, cx: number, cy: number, radius: number) {
    // Dark inner shadow arc
    ctx.strokeStyle = "#2a1608";
    ctx.lineWidth = 11;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, Math.PI * 1.1, Math.PI * 1.9);
    ctx.stroke();
    // Base wood color
    ctx.strokeStyle = "#7a4a1e";
    ctx.lineWidth = 8;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, Math.PI * 1.1, Math.PI * 1.9);
    ctx.stroke();
    // Twine wrap bands — short alternating arc segments along the handle
    const wraps = 14;
    const a0 = Math.PI * 1.1;
    const a1 = Math.PI * 1.9;
    for (let i = 0; i < wraps; i++) {
      const ta = a0 + ((a1 - a0) * i) / wraps;
      const tb = a0 + ((a1 - a0) * (i + 0.85)) / wraps;
      ctx.strokeStyle = i % 2 === 0 ? "#c8893d" : "#5a3414";
      ctx.lineWidth = 7;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, ta, tb);
      ctx.stroke();
      // Thin highlight along the top edge of each wrap
      ctx.strokeStyle = "rgba(255, 220, 160, 0.55)";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(cx, cy, radius + 2.5, ta, tb);
      ctx.stroke();
    }
  }

  private drawRimLeaf(ctx: CanvasRenderingContext2D, x: number, y: number) {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(-0.5);
    ctx.fillStyle = "#2e6a26";
    ctx.beginPath();
    ctx.ellipse(0, 0, 8, 3.5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#1a4218";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(-7, 0);
    ctx.lineTo(7, 0);
    ctx.stroke();
    // small highlight
    ctx.fillStyle = "rgba(180, 230, 140, 0.6)";
    ctx.fillRect(-2, -2, 3, 1);
    ctx.restore();
  }

  private drawParticle(ctx: CanvasRenderingContext2D, p: Particle) {
    const a = 1 - p.life / p.maxLife;
    ctx.globalAlpha = Math.max(0, a);
    ctx.fillStyle = p.color;
    ctx.fillRect(Math.round(p.x - p.size / 2), Math.round(p.y - p.size / 2), p.size, p.size);
    ctx.globalAlpha = 1;
  }

  private drawFloat(ctx: CanvasRenderingContext2D, f: FloatText) {
    const a = 1 - f.life / f.maxLife;
    ctx.globalAlpha = Math.max(0, a);
    ctx.font = `bold ${f.size}px 'Press Start 2P', monospace`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "rgba(0,0,0,0.7)";
    ctx.fillText(f.text, f.x + 2, f.y + 2);
    ctx.fillStyle = f.color;
    ctx.fillText(f.text, f.x, f.y);
    ctx.globalAlpha = 1;
  }

  private drawScanlines(ctx: CanvasRenderingContext2D) {
    ctx.globalAlpha = 0.08;
    ctx.fillStyle = "#000";
    for (let y = 0; y < PLAY_H; y += 3) {
      ctx.fillRect(0, y, PLAY_W, 1);
    }
    ctx.globalAlpha = 1;
  }

  private drawVignette(ctx: CanvasRenderingContext2D) {
    const g = ctx.createRadialGradient(
      PLAY_W / 2, PLAY_H / 2, PLAY_W * 0.5,
      PLAY_W / 2, PLAY_H / 2, PLAY_W * 0.85,
    );
    g.addColorStop(0, "rgba(0,0,0,0)");
    g.addColorStop(1, "rgba(0,0,0,0.38)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, PLAY_W, PLAY_H);
  }
}

function mulberry32(seed: number) {
  return function () {
    seed |= 0;
    seed = (seed + 0x6D2B79F5) | 0;
    let t = seed;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
